import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { DatePicker, Input, Message, Placeholder } from 'rsuite';
import PageHeader from '../components/shared/PageHeader';
import ProfilePhoto from '../components/employees/ProfilePhoto';
import { useAuth } from '../contexts/AuthContext';
import {
  useCompleteOnboardingStep,
  useMyOnboarding,
  useNotifyOnboardingCoach,
  useRemoveOnboardingUpload,
  useSetOnboardingChecklistItem,
  useSetOnboardingStepSchedule,
  useUploadOnboardingFile,
} from '../hooks/useQueries';
import { formatBytes, getFileExtension } from '../utils/expenseAttachments';
import { showToast } from '../utils/toast';
import './OnboardingPage.css';

// A date-only string to a Date the picker can hold, and back, both in local
// time. Going through `new Date('2026-04-01')` would parse it as UTC midnight
// and show the day before to anyone west of Greenwich.
const dateOnlyToLocal = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const localToDateOnly = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * What a profile step is waiting on.
 *
 * Every field the step asks for, in its group, ticked or not — so the answer to
 * "what is still missing" is on the step itself rather than something to go and
 * work out on the profile page.
 *
 * An optional field is listed the same way but reads "Optional" rather than
 * "Missing", because leaving it blank is not something to go and fix — the step
 * confirms without it.
 */
function ProfileChecklist({ status }) {
  if (!status || status.groups.length === 0) return null;

  return (
    <div className="onb-checklist">
      {status.groups.map((group) => (
        <div className="onb-checklist__group" key={group.key}>
          <div className="onb-checklist__grouphead">
            <span className="onb-checklist__grouplabel">{group.label}</span>
            {group.complete ? (
              <span className="onb-chip onb-chip--done">
                <i className="bi bi-check-circle-fill" aria-hidden="true" />
                Complete
              </span>
            ) : (
              <span className="onb-chip onb-chip--todo">Needs your input</span>
            )}
          </div>

          <ul className="onb-checklist__fields">
            {group.fields.map((field) => (
              <li
                className={`onb-checklist__field${field.complete ? ' onb-checklist__field--done' : ''}`}
                key={field.key}
              >
                <i
                  className={`bi ${field.complete ? 'bi-check-circle-fill' : (field.optional ? 'bi-dash-circle' : 'bi-circle')}`}
                  aria-hidden="true"
                />
                <span>{field.label}</span>
                <span className="onb-checklist__state">
                  {field.complete ? 'Entered' : (field.optional ? 'Optional' : 'Missing')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * The boxes on a checklist step.
 *
 * Each one is a real checkbox with its text beside it, and its own link when it
 * has one. Ticks are saved as they are made, so the list is where it was left
 * after a reload; the step will not confirm until every box is ticked.
 */
function StepChecklist({ status, disabled, onToggle }) {
  if (!status || status.items.length === 0) return null;

  return (
    <ul className="onb-boxes">
      {status.items.map((item) => (
        <li className={`onb-box${item.checked ? ' onb-box--checked' : ''}`} key={item.id}>
          <label className="onb-box__label">
            <input
              type="checkbox"
              className="onb-box__input"
              checked={item.checked}
              disabled={disabled}
              onChange={(event) => onToggle(item.id, event.target.checked)}
            />
            <span>{item.label}</span>
          </label>

          {item.link_url ? (
            <a
              className="bdc-btn bdc-btn--sm onb-box__link"
              href={item.link_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
              {item.link_label || 'Open'}
            </a>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * The person a step points at.
 *
 * Either the employee's own coach, resolved per person, or somebody named when
 * the step was written. It rides beside the date rather than under it, so the
 * step reads as one thing: when, and who with.
 */
function StepPerson({ person }) {
  if (!person) return null;

  return (
    <aside className="onb-person">
      <div className="onb-person__label">
        {person.source === 'coach' ? 'Your coach' : 'Who to contact'}
      </div>

      <div className="onb-person__row">
        <ProfilePhoto
          employeeId={person.employee_id}
          size="small"
          firstName={person.first_name}
          lastName={person.last_name}
        />

        <div className="onb-person__who">
          <div className="onb-person__name">{person.name}</div>
          {person.job_title ? <div className="onb-person__title">{person.job_title}</div> : null}
          {person.email ? (
            <a className="onb-person__email" href={`mailto:${person.email}`}>{person.email}</a>
          ) : null}
        </div>
      </div>
    </aside>
  );
}

/**
 * The date entry on an event step.
 *
 * Saved as it is picked rather than behind a save button of its own: the step
 * already has one button, and a date entered but not saved would be a second
 * thing to get wrong.
 */
function StepSchedule({ status, disabled, onChange }) {
  const value = dateOnlyToLocal(status?.scheduled_on);

  return (
    <div className="onb-schedule">
      <span className="onb-schedule__label">Date scheduled</span>
      <DatePicker
        value={value}
        onChange={(next) => {
          const dateOnly = localToDateOnly(next);
          if (dateOnly) onChange(dateOnly);
        }}
        disabled={disabled}
        format="MM/dd/yyyy"
        placeholder="Pick the date"
        cleanable={false}
        oneTap
        /*
          The journey is a long page and this step can be the last thing on it,
          where a calendar opening downwards runs off the bottom of the screen.
          autoVerticalStart opens it upwards when that is where the room is, and
          preventOverflow nudges it back on screen in the cases where neither
          side has quite enough.
        */
        placement="autoVerticalStart"
        preventOverflow
      />
    </div>
  );
}

/**
 * The written answer on an input step.
 *
 * Just the box. It has no save of its own: Confirm and continue is the save,
 * so the step keeps the one button every other kind has and the employee is
 * never left wondering whether a saved answer still needs confirming.
 *
 * The draft therefore lives in the step above, which owns that button.
 */
function StepResponse({ status, value, onChange, disabled }) {
  const maxLength = status?.max_length || 4000;
  const length = value.trim().length;

  return (
    <div className="onb-response">
      <Input
        as="textarea"
        rows={5}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder="Write your answer here"
      />

      {/* Only once it is worth watching. A counter from the first character
          reads as a limit being enforced rather than a length being kept an
          eye on. */}
      {length > maxLength * 0.8 ? (
        <div className="onb-response__foot">
          <span className={`onb-response__count${length > maxLength ? ' onb-response__count--over' : ''}`}>
            {length} / {maxLength}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * What the employee sends back on an upload step. The document to fill in, when
 * the step has one, is its link, rendered above this like any other step's.
 *
 * Uploads go straight to their document library's Onboarding folder as they are
 * picked, and are listed here so they can see it worked. A wrong file can be
 * taken back while the step is still the one they are on; after that the list
 * is a record.
 */
function StepUpload({
  status,
  isCurrent,
  onUpload,
  uploading,
  onRemove,
  removingId,
}) {
  const inputRef = useRef(null);
  const uploads = status?.uploads || [];
  const accept = (status?.accepted_extensions || []).join(',');
  const libraryReady = status?.library_ready !== false;

  return (
    <div className="onb-upload">

      <div className="onb-upload__block">
        <div className="onb-upload__label">{isCurrent ? 'Upload' : 'Uploaded'}</div>

        {uploads.length > 0 ? (
          <ul className="onb-upload__list">
            {uploads.map((upload) => (
              <li className="onb-upload__file" key={upload.id}>
                <i className="bi bi-file-earmark-check onb-upload__icon onb-upload__icon--done" aria-hidden="true" />
                <div className="onb-upload__name">
                  <span>{upload.file_name}</span>
                  <span className="onb-upload__meta">
                    {[formatBytes(upload.size_bytes), formatDate(upload.uploaded_at)].filter(Boolean).join(' · ')}
                  </span>
                </div>
                {isCurrent ? (
                  <button
                    type="button"
                    className="bdc-iconbtn"
                    onClick={() => onRemove(upload.id)}
                    disabled={Boolean(removingId) || uploading}
                    aria-label={`Remove ${upload.file_name}`}
                    title="Remove this file"
                  >
                    <i className={`bi ${removingId === upload.id ? 'bi-hourglass-split' : 'bi-trash'}`} aria-hidden="true" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        {isCurrent && !libraryReady ? (
          <Message type="warning" showIcon>
            Your document library has not been set up yet, so there is nowhere to put an upload.
            Ask an administrator to set it up, then come back to this step.
          </Message>
        ) : null}

        {isCurrent && libraryReady ? (
          <div className="onb-upload__actions">
            <input
              ref={inputRef}
              type="file"
              accept={accept || undefined}
              multiple
              hidden
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                // Cleared straight away so picking the same file again still
                // fires a change.
                event.target.value = '';
                if (files.length > 0) onUpload(files);
              }}
            />
            <button
              type="button"
              className="bdc-btn"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              <i className="bi bi-upload" aria-hidden="true" />
              {uploading ? 'Uploading…' : uploads.length > 0 ? 'Upload another file' : 'Upload file'}
            </button>
            <span className="onb-upload__meta">
              Saved to the Onboarding folder in your documents.
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The signature on a signature step.
 *
 * The statement being agreed to sits directly above the line it is signed on,
 * and the name is typed in a cursive face so it reads as a signature rather
 * than a form field. Like an input step it has no save of its own: Sign and
 * continue is the act of signing, so the draft lives in the step above.
 *
 * Once signed it is shown back exactly as recorded, with the date, and cannot
 * be edited.
 */
function StepSignature({ statement, status, value, onChange, disabled, signed, placeholder }) {
  const maxLength = status?.max_length || 200;
  const shown = signed ? status?.signed_name || '' : value;
  const signedOn = signed ? formatDate(status?.signed_at) : null;

  return (
    <div className="onb-signature">
      {statement ? <p className="onb-signature__statement">{statement}</p> : null}

      <label className="onb-signature__field">
        <span className="onb-signature__label">
          {signed ? 'Signed' : 'Type your full name to sign'}
        </span>
        <input
          type="text"
          className="onb-signature__input"
          value={shown}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          readOnly={signed}
          maxLength={maxLength}
          placeholder={placeholder}
          autoComplete="name"
          spellCheck={false}
        />
      </label>

      {signedOn ? <div className="onb-signature__meta">Signed electronically on {signedOn}</div> : null}
    </div>
  );
}

/**
 * One step in the run.
 *
 * A step is in exactly one of three states, and the state is what it looks
 * like: done (ticked, muted, no controls), current (ringed, the only card with
 * a button on it), or locked (a title and nothing else — the server does not
 * send the content of a step nobody has reached yet).
 */
function JourneyStep({
  step,
  index,
  state,
  isLast,
  onComplete,
  completing,
  linkOpened,
  onOpenLink,
  onToggleChecklistItem,
  togglingChecklist,
  onSchedule,
  scheduling,
  signerName,
  onUpload,
  uploading,
  onRemoveUpload,
  removingUploadId,
  stepRef,
}) {
  const isCurrent = state === 'current';
  const isDone = state === 'done';
  const isLocked = state === 'locked';

  // A finished step keeps its number, its title and its badge, and folds the
  // rest away: the run is a record of what is done, not a wall of text already
  // read. It opens again for anyone who wants to go back over it.
  const [expanded, setExpanded] = useState(false);
  const showsContent = !isLocked && (!isDone || expanded);

  // A link step is confirmed only after the link has actually been opened.
  // Tracked for this visit only: the point is to stop somebody clicking past a
  // document they never opened, not to hold a record of who clicked what.
  // A signature step's link is optional; when it has one, it is the document
  // being signed, and it gets the same open-it-first rule.
  const needsSignature = step.kind === 'signature';
  const needsLink = step.kind === 'link' || (needsSignature && Boolean(step.link_url));
  const needsProfile = step.kind === 'profile';
  const needsChecklist = step.kind === 'checklist';
  const needsSchedule = step.kind === 'event';
  const needsResponse = step.kind === 'input';
  const needsUpload = step.kind === 'upload';

  // The answer in the box, which Confirm and continue sends. Seeded from
  // whatever is already stored so a step being re-read shows what was written.
  const savedResponse = step.input_status?.response_text || '';
  const [response, setResponse] = useState(savedResponse);
  useEffect(() => {
    setResponse(savedResponse);
  }, [savedResponse]);

  const responseMaxLength = step.input_status?.max_length || 4000;
  const responseText = response.trim();

  // Never seeded: a signature is typed fresh each time the step is confirmed,
  // so one left over from before a reset cannot be clicked straight through.
  const [signature, setSignature] = useState('');
  const signatureText = signature.replace(/\s+/g, ' ').trim();

  const profileComplete = needsProfile ? Boolean(step.profile_status?.complete) : true;
  const checklistComplete = needsChecklist ? Boolean(step.checklist_status?.complete) : true;
  const scheduleComplete = needsSchedule ? Boolean(step.event_status?.scheduled_on) : true;
  const responseComplete = needsResponse
    ? responseText.length > 0 && responseText.length <= responseMaxLength
    : true;
  const signatureComplete = needsSignature ? signatureText.length > 0 : true;
  const uploadLibraryReady = step.upload_status?.library_ready !== false;
  const uploadComplete = needsUpload ? (step.upload_status?.uploads || []).length > 0 : true;
  const canConfirm = isCurrent
    && (!needsLink || linkOpened)
    && signatureComplete
    && uploadComplete
    && profileComplete
    && checklistComplete
    && scheduleComplete
    && responseComplete;

  // Only ever says what is *blocking* the button. With nothing in the way the
  // button speaks for itself and the line is left off.
  const hint = needsLink && !linkOpened
    ? (needsSignature ? 'Open the document above first, then sign.' : 'Open the link above first, then confirm.')
    : needsSignature && !signatureComplete
      ? 'Type your full name above to sign.'
      : needsUpload && !uploadLibraryReady
        ? 'This step needs your document library set up first.'
        : needsUpload && !uploadComplete
          ? 'Upload your document above to continue.'
          : needsProfile && !profileComplete
      ? 'Enter the information above, then come back and confirm.'
      : needsChecklist && !checklistComplete
        ? 'Tick every box above to continue.'
        : needsSchedule && !scheduleComplete
          ? 'Enter the date you have scheduled this for to continue.'
          : needsResponse && responseText.length > responseMaxLength
            ? `That is longer than the ${responseMaxLength} characters this step allows.`
            : needsResponse && !responseComplete
              ? 'Write your answer above to continue.'
              : null;

  return (
    <div className={`onb-step onb-step--${state}`} data-step-id={step.id} ref={stepRef}>
      <div className="onb-step__rail" aria-hidden="true">
        <span className="onb-step__marker">
          {isDone ? <i className="bi bi-check-lg" /> : isLocked ? <i className="bi bi-lock-fill" /> : index + 1}
        </span>
        {!isLast ? <span className="onb-step__line" /> : null}
      </div>

      <div className={`onb-step__card${isDone && !expanded ? ' onb-step__card--folded' : ''}`}>
        {React.createElement(
          isDone ? 'button' : 'div',
          isDone
            ? {
              type: 'button',
              className: 'onb-step__head onb-step__head--toggle',
              onClick: () => setExpanded((prev) => !prev),
              'aria-expanded': expanded,
            }
            : { className: 'onb-step__head' },
          <>
            <div className="onb-step__heading">
              {isDone ? (
                <i className="bi bi-chevron-down onb-step__chevron" aria-hidden="true" />
              ) : null}
              <div>
                <div className="onb-step__eyebrow">Step {index + 1}</div>
                <h2 className="onb-step__title">{step.title}</h2>
              </div>
            </div>

            {isDone ? (
              <span className="onb-chip onb-chip--done">
                <i className="bi bi-check-circle-fill" aria-hidden="true" />
                Completed{formatDate(step.completed_at) ? ` · ${formatDate(step.completed_at)}` : ''}
              </span>
            ) : isCurrent ? (
              <span className="onb-chip onb-chip--current">You are here</span>
            ) : (
              <span className="onb-chip">
                <i className="bi bi-lock-fill" aria-hidden="true" />
                Locked
              </span>
            )}
          </>
        )}

        {isLocked ? null : (
          <div className="onb-step__content" data-open={showsContent ? 'true' : 'false'}>
            <div className="onb-step__contentinner" inert={showsContent ? undefined : ''}>
              {step.body && !needsSignature ? <p className="onb-step__body">{step.body}</p> : null}

              {needsProfile ? <ProfileChecklist status={step.profile_status} /> : null}

              {needsChecklist ? (
                <StepChecklist
                  status={step.checklist_status}
                  /* Not disabled while a tick is saving: doing that greyed out
                   every box on the step for the length of each round trip,
                   which read as the whole list flashing. The tick is applied
                   to the cache on click and rolled back if the save fails. */
                disabled={!isCurrent}
                  onToggle={(itemId, checked) => onToggleChecklistItem(step.id, itemId, checked)}
                />
              ) : null}

              {needsResponse ? (
                <StepResponse
                  status={step.input_status}
                  value={response}
                  onChange={setResponse}
                  disabled={!isCurrent || completing}
                />
              ) : null}

              {needsSchedule ? (
                <div className="onb-event">
                  <StepSchedule
                    status={step.event_status}
                    disabled={!isCurrent || scheduling}
                    onChange={(scheduledOn) => onSchedule(step.id, scheduledOn)}
                  />
                  <StepPerson person={step.person} />
                </div>
              ) : null}

              {/* An upload step's link is the document to fill in. It is shown
                  the same way, but not required to be opened first: the upload
                  is what proves they have it. */}
              {(needsLink || needsUpload) && step.link_url ? (
                <div className="onb-step__linkrow">
                  <a
                    className="bdc-btn"
                    href={step.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onOpenLink(step.id)}
                  >
                    <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                    {step.link_label || (needsSignature || needsUpload ? 'Open document' : 'Open link')}
                  </a>
                  {isCurrent && linkOpened ? (
                    <span className="onb-step__opened">
                      <i className="bi bi-check-lg" aria-hidden="true" />
                      Opened
                    </span>
                  ) : null}
                </div>
              ) : null}

              {needsSignature ? (
                <StepSignature
                  statement={step.body}
                  status={step.signature_status}
                  value={signature}
                  onChange={setSignature}
                  disabled={!isCurrent || completing}
                  signed={isDone}
                  placeholder={signerName || 'Your full name'}
                />
              ) : null}

              {needsUpload ? (
                <StepUpload
                  status={step.upload_status}
                  isCurrent={isCurrent}
                  onUpload={(files) => onUpload(step.id, files)}
                  uploading={uploading}
                  onRemove={(uploadId) => onRemoveUpload(step.id, uploadId)}
                  removingId={removingUploadId}
                />
              ) : null}

              {needsProfile && isCurrent && !profileComplete ? (
                <div className="onb-step__linkrow">
                  <Link className="bdc-btn" to={step.profile_status?.groups?.[0]?.href || '/me'}>
                    <i className="bi bi-pencil-square" aria-hidden="true" />
                    Update my info
                  </Link>
                </div>
              ) : null}

              {isCurrent ? (
                <div className="onb-step__foot">
                  {hint ? <span className="onb-step__hint">{hint}</span> : null}
                  <button
                    type="button"
                    className="bdc-btn bdc-btn--primary"
                    onClick={() => onComplete(
                      step.id,
                      needsResponse ? responseText : needsSignature ? signatureText : null
                    )}
                    disabled={!canConfirm || completing}
                    title={!canConfirm ? hint || undefined : undefined}
                  >
                    <i className="bi bi-check-lg" aria-hidden="true" />
                    {completing ? 'Saving…' : needsSignature ? 'Sign and continue' : 'Confirm and continue'}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The onboarding journey.
 *
 * Only reachable by somebody who has been put through onboarding and has not
 * finished it. Progress is per step and lives on the server, so closing the tab
 * and coming back lands on the same step.
 */
export default function OnboardingPage() {
  const auth = useAuth();
  // staleTime 0: a profile step sends people to /me and back, and the answer to
  // whether they filled it in has to be re-asked when they return.
  const query = useMyOnboarding({ enabled: true, staleTime: 0 });
  const completeMutation = useCompleteOnboardingStep();
  const checklistMutation = useSetOnboardingChecklistItem();
  const scheduleMutation = useSetOnboardingStepSchedule();
  const uploadMutation = useUploadOnboardingFile();
  const removeUploadMutation = useRemoveOnboardingUpload();
  const notifyCoach = useNotifyOnboardingCoach();
  const navigate = useNavigate();

  const [openedLinks, setOpenedLinks] = useState(() => new Set());
  const currentRef = useRef(null);
  const scrolledToRef = useRef(null);

  const state = query.data || null;
  const steps = useMemo(() => state?.steps || [], [state]);
  const currentStepId = state?.current_step_id || null;
  const total = steps.length;
  const completed = state?.completed_count || 0;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  /*
    Whether this visit began mid-journey.

    The page is meant to be invisible once onboarding is finished, but somebody
    who finishes it *while looking at it* should see that they finished rather
    than being thrown to the home page mid-click. So the redirect only applies
    to a visit that was already complete when it started.
  */
  const startedIncomplete = useRef(null);
  if (startedIncomplete.current === null && state) {
    startedIncomplete.current = state.assigned && state.status !== 'completed';
  }

  // Land on the step they left off at rather than the top of the run.
  useEffect(() => {
    if (!currentStepId || scrolledToRef.current === currentStepId) return;
    const node = currentRef.current;
    if (!node) return;
    scrolledToRef.current = currentStepId;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [currentStepId]);

  const handleOpenLink = (stepId) => {
    setOpenedLinks((prev) => {
      if (prev.has(stepId)) return prev;
      const next = new Set(prev);
      next.add(stepId);
      return next;
    });
  };

  const handleToggleChecklistItem = async (stepId, itemId, checked) => {
    try {
      await checklistMutation.mutateAsync({ stepId, itemId, checked });
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to save that tick'));
    }
  };

  const handleSchedule = async (stepId, scheduledOn) => {
    try {
      await scheduleMutation.mutateAsync({ stepId, scheduledOn });
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to save that date'));
    }
  };

  /*
    Uploads go one at a time, in the order picked. A file the step will not take
    is turned away here with the reason, before it is read and sent anywhere;
    the server checks the same things again.
  */
  const handleUpload = async (stepId, files) => {
    const status = steps.find((step) => step.id === stepId)?.upload_status;
    const accepted = new Set(status?.accepted_extensions || []);
    const maxBytes = status?.max_bytes || 0;

    for (const file of files) {
      const extension = getFileExtension(file.name);
      if (accepted.size > 0 && !accepted.has(extension)) {
        showToast('error', `"${file.name}" is not a file type this step accepts.`);
        continue;
      }
      if (maxBytes > 0 && file.size > maxBytes) {
        showToast('error', `"${file.name}" is larger than ${formatBytes(maxBytes)}.`);
        continue;
      }

      try {
        await uploadMutation.mutateAsync({ stepId, file });
      } catch (err) {
        showToast('error', String(err?.message || err || `Unable to upload "${file.name}"`));
      }
    }
  };

  const handleRemoveUpload = async (stepId, uploadId) => {
    try {
      await removeUploadMutation.mutateAsync({ stepId, uploadId });
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to remove that file'));
    }
  };

  /*
    The last act of the journey: leave the page.

    The coach is emailed on the way out, and none of that is the employee's
    business — no coach set, already sent, or a send that failed are all things
    they can do nothing about, so none of them reach the screen. The server logs
    what happened; this only has to get them off a page that is about to
    disappear on them, which it does whether the email went or not.
  */
  const handleFinish = async () => {
    try {
      await notifyCoach.mutateAsync();
    } catch {
      // Deliberately quiet — see above.
    } finally {
      navigate('/');
    }
  };

  const handleComplete = async (stepId, responseText = null) => {
    try {
      const result = await completeMutation.mutateAsync({ stepId, responseText });
      if (result?.status === 'completed') {
        showToast('success', 'That is onboarding done. Welcome aboard.');
      }
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to save that step'));
    }
  };

  if (query.isLoading || auth.isResolving) {
    return <Placeholder.Paragraph rows={8} active />;
  }

  if (query.error) {
    return (
      <div className="bdc-page">
        <Message type="error" showIcon header="Unable to load onboarding">
          {String(query.error?.message || query.error)}
        </Message>
      </div>
    );
  }

  // Not onboarding, or arrived here already finished: the page is not theirs.
  if (!state?.assigned || (state.status === 'completed' && startedIncomplete.current === false)) {
    return <Navigate to="/" replace />;
  }

  const isFinished = state.status === 'completed';
  const firstName = auth.employee?.first_name;
  const signerName = `${auth.employee?.first_name || ''} ${auth.employee?.last_name || ''}`.trim();

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Onboarding' }]}
        icon="bi-signpost-2"
        title={firstName ? `Welcome, ${firstName}` : 'Welcome aboard'}
        showBack={false}
      />

      {total === 0 ? (
        <Message type="info" showIcon header="Nothing to do yet">
          Your onboarding has been started, but no steps have been set up yet. Check back shortly —
          whoever set this up will add them.
        </Message>
      ) : (
        <>
          <div className="onb-progress">
            <div className="onb-progress__line">
              <span className="onb-progress__label">Your progress</span>
              <span className="onb-progress__value bdc-num">{percent}%</span>
            </div>
            <div
              className="onb-progress__track"
              role="progressbar"
              aria-valuenow={completed}
              aria-valuemin={0}
              aria-valuemax={total}
              aria-label="Onboarding progress"
            >
              <div className="onb-progress__fill" style={{ width: `${percent}%` }} />
            </div>
          </div>

          <div className="onb-journey">
            {steps.map((step, index) => {
              const stepState = step.completed
                ? 'done'
                : step.id === currentStepId
                  ? 'current'
                  : 'locked';

              return (
                <JourneyStep
                  key={step.id}
                  stepRef={stepState === 'current' ? currentRef : undefined}
                  step={step}
                  index={index}
                  state={stepState}
                  isLast={index === steps.length - 1}
                  onComplete={handleComplete}
                  completing={completeMutation.isPending}
                  linkOpened={openedLinks.has(step.id)}
                  onOpenLink={handleOpenLink}
                  onToggleChecklistItem={handleToggleChecklistItem}
                  togglingChecklist={checklistMutation.isPending}
                  onSchedule={handleSchedule}
                  scheduling={scheduleMutation.isPending}
                  signerName={signerName}
                  onUpload={handleUpload}
                  uploading={uploadMutation.isPending}
                  onRemoveUpload={handleRemoveUpload}
                  removingUploadId={removeUploadMutation.isPending ? removeUploadMutation.variables?.uploadId : null}
                />
              );
            })}
          </div>

          {isFinished ? (
            <div className="onb-finished">
              <i className="bi bi-patch-check-fill onb-finished__icon" aria-hidden="true" />
              <h2 className="onb-finished__title">Onboarding complete</h2>
              <p className="onb-finished__note">
                You have worked through every step. This page will not appear again — everything else
                you need is on the home page.
              </p>
              <button
                type="button"
                className="bdc-btn bdc-btn--primary"
                onClick={handleFinish}
                disabled={notifyCoach.isPending}
              >
                <i className="bi bi-check-lg" aria-hidden="true" />
                {notifyCoach.isPending ? 'Finishing…' : 'Finish'}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
